import { ContextUrlInput } from "@/components/ContextUrlInput";
import { EnvironmentClassSelect } from "@/components/EnvironmentClassSelect";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock, SkeletonText } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { NO_PROJECT_SELECTED, ProjectSelect } from "@/components/projects/ProjectSelect";
import { useListContextUrls, useSetContextUrls } from "@/hooks/use-context-url";
import { sortEnvironmentClassEntries, useEnvironmentClassEntries } from "@/hooks/use-environment-class-entries";
import { useEnvironmentClassSelection } from "@/hooks/use-environment-class-selection";
import { useRequireProject } from "@/hooks/use-require-project";
import {
    NoGitInitializerFoundError,
    type PlainEnvironment,
    useCreateEnvironment,
    useCreateEnvironmentFromProject,
} from "@/queries/environment-queries";
import { isInvalidArgumentError, isNotFoundError, isUnauthorizedError } from "@/queries/errors";
import { type PlainProject, useListProjectEnvironmentClasses, useListProjects } from "@/queries/project-queries";
import { useListHostAuthenticationTokens } from "@/queries/runner-configuration-queries";
import { useIsUserAuthenticatedWithRunner, useParseContextURL } from "@/queries/runner-queries";
import {
    escapePressCallbackAtom,
    selectedClassEntryAtom,
    type CreateEnvironmentStage,
    isNoSuitableRunnerFoundError,
} from "@/routes/environments/create/create-environment-utils";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError } from "@/utils/errors";
import { Code, ConnectError } from "@connectrpc/connect";
import { getScmProviderName } from "frontend-shared/local-runner";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { ParseContextURLPreconditionFailureDetails, RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { type FC, type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { EnvironmentCreateError } from "@/routes/environments/create/components/EnvironmentCreateError";
import { NoEnvironmentClassesError } from "@/routes/environments/create/components/NoEnvironmentClassesError";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { useAtom, useSetAtom } from "jotai";

export const RepositoryTab: FC<{
    disableProjectSelection?: boolean;
    contextURL?: string;
    autoStart?: boolean;
    contextURLValid: boolean;
    selectedProject: PlainProject | typeof NO_PROJECT_SELECTED | undefined;
    setSelectedProject: (project: PlainProject | typeof NO_PROJECT_SELECTED | undefined) => void;
    setAutoStartActive: (autoStart: boolean) => void;
    setContextURL: (contextURL: string) => void;
    onClose: () => void;
    setStage: (stage: CreateEnvironmentStage) => void;
}> = ({
    contextURL,
    contextURLValid,
    autoStart = false,
    disableProjectSelection = false,
    selectedProject,
    setAutoStartActive,
    setSelectedProject,
    setContextURL,
    onClose,
    setStage,
}) => {
    const [selectedClassEntry, setSelectedClassEntry] = useAtom(selectedClassEntryAtom);
    const navigate = useNavigate();
    const { contextUrls } = useListContextUrls();
    const setContextUrls = useSetContextUrls();
    const { toast } = useToast();

    const [createError, setCreateError] = useState<Error | null>(null);
    const isSubmittingRef = useRef(false);

    const { requireProjects } = useRequireProject();
    const { data: projectEnvironmentClasses, isLoading: isLoadingProjectClasses } = useListProjectEnvironmentClasses(
        selectedProject && selectedProject !== NO_PROJECT_SELECTED ? selectedProject.id : undefined,
    );

    const { data: userConfiguredSCMIntegrations } = useListHostAuthenticationTokens({});

    const [debouncedContext] = useDebounce({ contextURL, contextURLValid }, 1000, {
        trailing: true,
        equalityFn: (left, right) => {
            return left.contextURL == right.contextURL && left.contextURLValid == right.contextURLValid;
        },
    });
    const isTypingContextURL = contextURL !== debouncedContext.contextURL;

    const { environmentClassEntries, isLoading: isLoadingEnvClasses } = useEnvironmentClassEntries();
    const sortedEnvironmentClassEntries = useMemo(() => {
        if (!userConfiguredSCMIntegrations) {
            return [];
        }

        // First apply CPU-based sorting, then prioritize by SCM authentication
        const cpuSorted = sortEnvironmentClassEntries(environmentClassEntries);

        // classes belonging to runners where the user has authenticated with the relevant SCM provider come first
        return cpuSorted.sort((a, b) => {
            if (a.runner?.runnerId === b.runner?.runnerId || !contextURL || !contextURLValid) {
                return 0;
            }

            let host: string;
            try {
                host = new URL(contextURL).host;
            } catch {
                // If URL parsing fails (e.g., due to invalid input during typing), skip sorting by SCM auth
                return 0;
            }

            const aConfigured = userConfiguredSCMIntegrations.tokens.some(
                (token) => token.runnerId === a.runner?.runnerId && token.host === host,
            );
            const bConfigured = userConfiguredSCMIntegrations.tokens.some(
                (token) => token.runnerId === b.runner?.runnerId && token.host === host,
            );

            return (bConfigured ? 1 : 0) - (aConfigured ? 1 : 0);
        });
    }, [contextURL, contextURLValid, environmentClassEntries, userConfiguredSCMIntegrations]);
    const hasNoEnvClasses = !isLoadingEnvClasses && environmentClassEntries.length === 0;
    const hasProject = selectedProject && selectedProject !== NO_PROJECT_SELECTED;

    const {
        isLoading: isLoadingParseContext,
        isSuccess: isSuccessParseContext,
        error: errorParseContext,
        data: parseContextResponse,
        refetch: refetchParseContext,
    } = useParseContextURL(
        undefined, // not providing a runner ID here means we're always doing a lookup with all runners
        debouncedContext.contextURL,
        {
            enabled: debouncedContext.contextURLValid,
        },
    );

    const { data: projects, isLoading: isLoadingProjects } = useListProjects();
    const filteredProjects = useMemo(() => {
        if (isLoadingParseContext || !projects?.projects) {
            return undefined;
        }

        const filtered = projects.projects.filter((p) => parseContextResponse?.projectIds?.includes(p.id)) ?? [];

        // Sort by user count (totalSubjects) in descending order to prioritize most popular projects
        return filtered.sort((a, b) => {
            const aUserCount = a.usedBy?.totalSubjects ?? 0;
            const bUserCount = b.usedBy?.totalSubjects ?? 0;
            return bUserCount - aUserCount;
        });
    }, [projects, parseContextResponse, isLoadingParseContext]);
    // project selection lifecycle effect
    useEffect(() => {
        if (isLoadingProjects || !filteredProjects || disableProjectSelection || isLoadingEnvClasses) {
            return;
        }

        // if the user has selected "no project", we don't do anything
        if (selectedProject === NO_PROJECT_SELECTED) {
            return;
        }

        if (selectedProject) {
            // if the user has selected a project, we override it if it's not an option in the current list of projects
            const projectInList = filteredProjects.some((p) => p.id === selectedProject?.id);
            if (!projectInList) {
                setSelectedProject(filteredProjects[0] ?? NO_PROJECT_SELECTED);
            }

            return;
        }

        // if there are projects, we select the first one for the user
        if (filteredProjects.length > 0) {
            const project = filteredProjects[0];
            setSelectedProject(project);
        } else {
            // if there are no projects to choose from, we set the "no project" choice
            setSelectedProject(NO_PROJECT_SELECTED);
            if (autoStart) {
                setAutoStartActive(false);
                setAutoStartBannerState({
                    show: true,
                    message: "Because there are no projects to choose from, auto-start was disabled.",
                });
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredProjects, sortedEnvironmentClassEntries]);

    const [contextGitRefType, contextGitRef] = useMemo(() => {
        if (parseContextResponse?.git?.tag) {
            return ["tag", parseContextResponse.git.tag] as const;
        }
        if (parseContextResponse?.git?.branch) {
            return ["branch", parseContextResponse.git.branch] as const;
        }
        if (parseContextResponse?.git?.commit) {
            return ["commit", parseContextResponse.git.commit.slice(0, 7)] as const;
        }

        return [undefined, undefined] as const;
    }, [parseContextResponse]);

    const parseContextFailed = !isLoadingParseContext && isUnauthorizedError(errorParseContext);
    // if no runners can parse the context, we will get a different 404 we're not looking for. Hence the naive "runner" check
    const isNotFound =
        isNotFoundError(errorParseContext) &&
        errorParseContext instanceof ConnectError &&
        !isNoSuitableRunnerFoundError(errorParseContext);
    const isInvalidArgument = isInvalidArgumentError(errorParseContext);

    const {
        data: authCheckResponse,
        isLoading: isLoadingAuthCheck,
        isError: isErrorAuthCheck,
        error: errorAuthCheck,
        refetch: refetchAuthCheck,
    } = useIsUserAuthenticatedWithRunner(selectedClassEntry?.runner?.runnerId, debouncedContext.contextURL, {
        refetchUntilAuthenticated: true,

        // Auth check should start be polled for once the parseContextURL failed with an unauthorized error
        enabled: debouncedContext.contextURLValid,
    });
    const isAuthenticationRequired =
        parseContextFailed ||
        authCheckResponse?.type == "AuthenticationRequired" ||
        (isNoSuitableRunnerFoundError(errorParseContext) && !isErrorAuthCheck); // when something goes wrong with the auth check, we don't want to show the auth button.

    const scmName = useMemo(() => {
        let scmID = "";
        if (isAuthenticationRequired) {
            if (parseContextFailed) {
                if (errorParseContext instanceof ConnectError) {
                    const details = errorParseContext.findDetails(ParseContextURLPreconditionFailureDetails);
                    for (const detail of details) {
                        if (detail.scmId) {
                            scmID = detail.scmId;
                        }
                    }
                }
            }

            // we use the auth check response as a fallback, since there are cases where the backend does not populate the scmId field (like for our default global runner context resolution).
            if (!scmID && authCheckResponse?.type == "AuthenticationRequired") {
                return authCheckResponse.scmName;
            }

            return getScmProviderName(scmID);
        }
    }, [isAuthenticationRequired, parseContextFailed, errorParseContext, authCheckResponse]);

    const createEnvironment = useCreateEnvironment();
    const createEnvironmentFromProject = useCreateEnvironmentFromProject();

    const showClassSelection = useMemo(() => {
        // if we get the "runner not found" error, it means we need to auth with any specific runner, so we begin by showing the class selection
        if (contextURLValid && isNoSuitableRunnerFoundError(errorParseContext)) {
            return true;
        }

        if (disableProjectSelection) {
            return true;
        }

        // Show class selection when there's a valid context URL and either:
        // 1. No project is selected (NO_PROJECT_SELECTED), or
        // 2. No project is set yet (undefined) and projects are not required
        return contextURLValid && (selectedProject === NO_PROJECT_SELECTED || (!selectedProject && !requireProjects));
    }, [contextURLValid, errorParseContext, disableProjectSelection, selectedProject, requireProjects]);

    const selectedAuthClassEntry = useMemo(() => {
        if (errorParseContext instanceof ConnectError) {
            const details = errorParseContext.findDetails(ParseContextURLPreconditionFailureDetails);
            if (details) {
                for (const detail of details) {
                    if (detail.runnerIds.length > 0) {
                        return environmentClassEntries.find((entry) => {
                            return detail.runnerIds.includes(entry.runner?.runnerId ?? "");
                        });
                    }
                }
            }

            if (isNoSuitableRunnerFoundError(errorParseContext)) {
                return environmentClassEntries.find((entry) => {
                    return entry.runner?.status?.phase === RunnerPhase.ACTIVE;
                });
            }
        }
        return undefined;
    }, [environmentClassEntries, errorParseContext]);

    // Manage environment class selection lifecycle
    useEnvironmentClassSelection({
        isLoadingEnvClasses,
        isLoadingProjectClasses,
        isLoadingProjects,
        isValidContext: contextURLValid && !isLoadingParseContext && !!parseContextResponse,
        selectedProject,
        selectedClassEntry,
        sortedEnvironmentClassEntries,
        projectEnvironmentClasses,
        selectedAuthClassEntry,
        isAuthenticationRequired,
        setSelectedClassEntry,
    });

    const handleOnContinue = useCallback(async () => {
        // Prevent duplicate submissions
        if (isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;
        let environment: PlainEnvironment;
        try {
            setCreateError(null);
            if (hasProject) {
                environment = await createEnvironmentFromProject.mutateAsync({
                    project: selectedProject,
                    gitInitializerParams: {
                        ref: contextGitRef,
                        refType: contextGitRefType,
                    },
                });
            } else {
                environment = await createEnvironment.mutateAsync({
                    type: "contextUrl",
                    contextURL: contextURL || "",
                    classID: selectedClassEntry?.clazz?.id || "",
                });
            }
            if (contextURL) {
                await setContextUrls([contextURL, ...(contextUrls || []).filter((s) => s !== contextURL)]);
            }
            onClose();
            navigate(getDetailsURL({ environment }));
        } catch (error) {
            setCreateError(error instanceof Error ? error : new Error(String(error)));
            toast({ title: "Failed to create an environment", description: formatError(error) });
        } finally {
            isSubmittingRef.current = false;
        }
    }, [
        hasProject,
        contextURL,
        onClose,
        navigate,
        createEnvironmentFromProject,
        selectedProject,
        contextGitRef,
        contextGitRefType,
        createEnvironment,
        selectedClassEntry?.clazz?.id,
        setContextUrls,
        contextUrls,
        toast,
    ]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            const formData = new FormData(event.currentTarget);
            const runnerID = selectedClassEntry?.runner?.runnerId;
            const classID = selectedClassEntry?.clazz?.id;
            const contextURL = formData.get("contextURL") as string;

            // The form action is to disable auto-start if we're currently auto-starting
            if (autoStart) {
                setAutoStartActive(false);
                return;
            }

            if (!runnerID || !contextURL || !classID) {
                console.log(`Invalid runnerId=${runnerID} or contextURL=${contextURL} or classID=${classID}`);
                return;
            }

            if (authCheckResponse?.type == "AuthenticationRequired") {
                setStage({
                    type: "scm-authentication",
                    authenticationUrl: authCheckResponse.url,
                    patAuth: authCheckResponse.patAuth,
                    scmId: authCheckResponse.scmId,
                    scmName: authCheckResponse.scmName,
                    runner: selectedClassEntry?.runner,
                    contextUrl: contextURL,
                    onClickContinue: () => {
                        setCreateError(null);
                        void refetchParseContext();
                        void refetchAuthCheck();
                    },
                });
                return;
            }

            await handleOnContinue();
        },
        [
            selectedClassEntry?.runner,
            selectedClassEntry?.clazz,
            autoStart,
            authCheckResponse,
            handleOnContinue,
            refetchParseContext,
            refetchAuthCheck,
            setAutoStartActive,
            setStage,
        ],
    );

    const scmSettingsLink = useMemo(() => {
        if (selectedClassEntry === undefined) {
            return undefined;
        }

        // Remote runners have their own settings page. Local runners are configured on an org level in the runner settings.
        return selectedClassEntry.runner.kind === RunnerKind.REMOTE
            ? `/settings/runners/${selectedClassEntry.runner.runnerId}`
            : "/settings/runners";
    }, [selectedClassEntry]);

    const createButtonDisabled = useMemo(() => {
        // we only disable the auth button when there's an issue with our host auth check
        if (isAuthenticationRequired && selectedClassEntry) {
            return isErrorAuthCheck;
        }

        return (
            selectedClassEntry === undefined ||
            (!hasProject && requireProjects) ||
            !contextURLValid ||
            !isSuccessParseContext ||
            isErrorAuthCheck ||
            !!createError
        );
    }, [
        isAuthenticationRequired,
        selectedClassEntry,
        hasProject,
        requireProjects,
        contextURLValid,
        isSuccessParseContext,
        isErrorAuthCheck,
        createError,
    ]);

    const setEscapePressCallback = useSetAtom(escapePressCallbackAtom);
    const [autoStartBannerState, setAutoStartBannerState] = useState<{
        show: boolean;
        message: string;
    }>({ show: false, message: "" });
    // The autostart lifecycle effect.
    // Importantly, it takes care of creating the environment when all requirements are met and
    // aborting when we have an input error / failed precondition.
    useEffect(() => {
        if (!autoStart) {
            setEscapePressCallback(null);
            return;
        }

        setEscapePressCallback({
            fn: () => {
                setAutoStartActive(false);
            },
        });
        if (isAuthenticationRequired) {
            setAutoStartActive(false);
            return;
        }
        if (createButtonDisabled || selectedProject === NO_PROJECT_SELECTED) {
            if (
                !isLoadingParseContext &&
                !isLoadingAuthCheck &&
                !isLoadingEnvClasses &&
                !isLoadingParseContext &&
                !isLoadingProjects &&
                !isLoadingProjectClasses
            ) {
                setAutoStartActive(false);
            }
        } else {
            void handleOnContinue();
            setAutoStartActive(false);
        }

        return () => {
            setEscapePressCallback(null);
        };
    }, [
        autoStart,
        autoStartBannerState.show,
        contextURLValid,
        createButtonDisabled,
        handleOnContinue,
        isAuthenticationRequired,
        isLoadingAuthCheck,
        isLoadingEnvClasses,
        isLoadingParseContext,
        isLoadingProjects,
        isLoadingProjectClasses,
        selectedProject,
        setAutoStartActive,
        setEscapePressCallback,
    ]);

    const createButtonText = useMemo(() => {
        if (isAuthenticationRequired) {
            return "Authenticate";
        }
        if (autoStart) {
            return (
                <span className="flex items-center gap-0.5">
                    <IconSpinner className="animate-spin" size="base" aria-hidden={true} />
                    Auto-starting – cancel with <KeyboardKey className="bg-surface-invert-tertiary">Esc</KeyboardKey>
                </span>
            );
        }

        return "Create";
    }, [isAuthenticationRequired, autoStart]);

    const showIsVerifying = useMemo(
        () =>
            (!isAuthenticationRequired && contextURLValid && isLoadingParseContext && !hasNoEnvClasses) ||
            (isLoadingAuthCheck && isErrorAuthCheck),
        [
            isAuthenticationRequired,
            contextURLValid,
            isLoadingParseContext,
            hasNoEnvClasses,
            isLoadingAuthCheck,
            isErrorAuthCheck,
        ],
    );

    const showAccessSettingsButton = useMemo(
        () => !isTypingContextURL && isInvalidArgument && !!scmSettingsLink,
        [isTypingContextURL, isInvalidArgument, scmSettingsLink],
    );

    return (
        <form onSubmit={handleSubmit} className="overflow-y-auto px-1 pb-1 pt-6">
            <div className="flex flex-col gap-4" id="create-environment-form">
                {autoStartBannerState.show && (
                    <Banner className="flex flex-col gap-2" variant="info" text={autoStartBannerState.message} />
                )}

                <ContextUrlInput
                    label="Repository URL"
                    name="contextURL"
                    value={contextURL}
                    onChange={setContextURL}
                    errorMessage={!!contextURL && !contextURLValid ? "The URL must point to a Git repository." : ""}
                    autoFocus
                />

                {contextURLValid && (parseContextResponse || isLoadingParseContext) && (
                    <div className="flex flex-col gap-2">
                        <SkeletonText size="base" className="w-20 text-base" ready={!!contextGitRefType}>
                            <span className="text-base font-medium capitalize">{contextGitRefType}</span>
                        </SkeletonText>
                        <div className="mb-4 flex flex-col gap-2 rounded-lg bg-surface-02 px-4 py-2 font-medium">
                            <SkeletonText size="base" className="w-64 max-w-full text-lg" ready={!!contextGitRefType}>
                                {contextGitRefType === "branch" ? contextGitRef : <code>{contextGitRef}</code>}
                            </SkeletonText>
                        </div>
                    </div>
                )}

                {contextURL && contextURLValid && !disableProjectSelection && (
                    <SkeletonBlock className="h-16" ready={!isLoadingParseContext && !isLoadingProjects}>
                        <ProjectSelect
                            label="Project"
                            value={selectedProject === NO_PROJECT_SELECTED ? undefined : selectedProject}
                            onChange={(v) => {
                                const value = v ?? NO_PROJECT_SELECTED;
                                setSelectedProject(value);

                                // if the project is faulty, changing it will reset the error
                                if (createError instanceof NoGitInitializerFoundError) {
                                    setCreateError(null);
                                }
                                if (
                                    createError instanceof ConnectError &&
                                    createError.code === Code.FailedPrecondition
                                ) {
                                    setCreateError(null);
                                }
                            }}
                            name="project"
                            loading={isLoadingProjects}
                            disabled={!contextURLValid}
                            projects={filteredProjects ?? []}
                        />
                    </SkeletonBlock>
                )}

                {showClassSelection && (
                    <SkeletonBlock className="h-16" ready={disableProjectSelection || !isLoadingParseContext}>
                        <EnvironmentClassSelect
                            label="Environment class"
                            value={selectedClassEntry}
                            onChange={setSelectedClassEntry}
                            name="environment-class"
                            loading={isLoadingEnvClasses}
                            disabled={!contextURLValid && !disableProjectSelection}
                            environmentClassEntries={sortedEnvironmentClassEntries}
                        />
                    </SkeletonBlock>
                )}

                <div data-testid="errors" className="flex flex-col items-center justify-center gap-2 text-sm">
                    {showIsVerifying && <span data-testid="verifying">Verifying repository and connection…</span>}
                    {isAuthenticationRequired && (
                        <span className="text-wrap text-center">
                            <span className="font-bold">{selectedClassEntry?.runner?.name || "Unknown runner"}</span>{" "}
                            requires authentication with <span className="font-bold">{scmName}</span>
                        </span>
                    )}
                    {isNotFound && <span>The repository could not be found. Please check the URL and try again.</span>}
                    {requireProjects && !hasProject && parseContextResponse && (
                        <span>
                            Your organization requires you to create environments from projects.{" "}
                            {filteredProjects && filteredProjects.length > 0 ? (
                                <span>Please select a project from the list above to continue.</span>
                            ) : (
                                <span>Please contact your administrator to create a project.</span>
                            )}
                        </span>
                    )}
                    {isErrorAuthCheck && <EnvironmentCreateError error={errorAuthCheck} />}
                    {hasNoEnvClasses && <NoEnvironmentClassesError onClose={onClose} />}
                    {!showIsVerifying &&
                        !isAuthenticationRequired &&
                        !isNotFound &&
                        !hasNoEnvClasses &&
                        !isTypingContextURL &&
                        !!errorParseContext && <EnvironmentCreateError error={errorParseContext} />}
                    {createError && <EnvironmentCreateError error={createError} />}
                </div>

                <div className="flex flex-col gap-2">
                    {showAccessSettingsButton && scmSettingsLink && (
                        <Button variant="secondary" className="w-full" asChild>
                            <Link
                                to={scmSettingsLink}
                                onClick={onClose}
                                data-testid="open-repository-settings-link"
                                data-tracking-id="open-repository-access-settings-repository-tab"
                            >
                                Open repository access settings
                            </Link>
                        </Button>
                    )}
                    <Button
                        type="submit"
                        className={cn("w-full text-base font-bold", showAccessSettingsButton && "hidden")}
                        loading={createEnvironment.isPending || createEnvironmentFromProject.isPending}
                        disabled={
                            (!autoStart && createButtonDisabled) ||
                            createEnvironment.isSuccess ||
                            createEnvironmentFromProject.isSuccess
                        }
                        data-testid="create-environment-button"
                        data-tracking-id="create-environment-repository-tab"
                    >
                        {createButtonText}
                    </Button>
                </div>
            </div>
        </form>
    );
};
